// Label-anchor math shared by the DMA choropleth and the wizard's DMA
// picker. Extracted from DmaChoropleth (CTV-674) so both maps anchor names
// the same way.

// Area-weighted centroid + |area| of a polygon's outer ring (shoelace formula).
// The centroid follows the shape's actual mass, so it reads as centered for
// irregular/coastal markets where a bbox center would sit off to one side.
export function ringCentroid(ring: number[][]): { area: number; cx: number; cy: number } {
  let a = 0;
  let cx = 0;
  let cy = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [x0, y0] = ring[i] as [number, number];
    const [x1, y1] = ring[(i + 1) % n] as [number, number];
    const cross = x0 * y1 - x1 * y0;
    a += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  a *= 0.5;
  if (Math.abs(a) < 1e-12) {
    // Degenerate ring: average the vertices.
    let sx = 0;
    let sy = 0;
    for (const pt of ring) {
      sx += (pt as [number, number])[0];
      sy += (pt as [number, number])[1];
    }
    return { area: 0, cx: sx / Math.max(1, n), cy: sy / Math.max(1, n) };
  }
  return { area: Math.abs(a), cx: cx / (6 * a), cy: cy / (6 * a) };
}

// A point to anchor a region's name inside its border: the area-weighted
// centroid of its largest polygon (so islands in a MultiPolygon don't drag the
// label off the mainland).
export function featureLabelPoint(geom: GeoJSON.Geometry): [number, number] | null {
  const outerRings: number[][][] =
    geom.type === 'Polygon'
      ? [geom.coordinates[0] as number[][]]
      : geom.type === 'MultiPolygon'
        ? (geom.coordinates as number[][][][]).map((poly) => poly[0] as number[][])
        : [];
  let best: { area: number; center: [number, number] } | null = null;
  for (const ring of outerRings) {
    if (!ring || ring.length < 3) continue;
    const c = ringCentroid(ring);
    if (!best || c.area > best.area) best = { area: c.area, center: [c.cx, c.cy] };
  }
  return best ? best.center : null;
}
